/**
 * PosTerminalsAdmin.tsx — Admin > Payments > Forward > POS Terminals.
 *
 * Legacy's Point of Sale Terminals (admin/admin_terminal_forward_list.php): the
 * account's card terminals, which the charge window lists. In the Lead Sources
 * layout (see components/admin/AdminList.tsx):
 *   - Title, HSN and Merchant, sorted by title; search by title or HSN; Show
 *     deleted;
 *   - "Add terminal" top right; a click on a live row opens it; Edit / Delete on
 *     a live row, Restore on a deleted one;
 *   - legacy's form: Title *, HSN * (the terminal's serial number) and Merchant —
 *     required here, as a terminal without one cannot connect. A new terminal
 *     starts on the default merchant.
 * On a Locked page (useAdminPageLevel) there is no Add, no row icon and no
 * Actions column; a live terminal still opens, read-only.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Plus } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import { Input } from '@/components/ui/Input';
import { Select } from '@/components/ui/Select';
import { SpinnerOverlay } from '@/components/ui/Spinner';
import {
  ACTIONS_COLUMN,
  ADMIN_TD_CLASS,
  AdminListEmpty,
  AdminListPage,
  AdminListTable,
  AdminListToolbar,
  AdminRowName,
  PaginationFooter,
  adminRowClass,
  type AdminListColumn,
} from '@/components/admin/AdminList';
import { AdminFormModal } from '@/components/admin/AdminFormModal';
import { AdminRowAction, AdminRowActions } from '@/components/admin/AdminRowActions';
import { useAdminPageLevel } from '@/components/admin/AdminPageLevelContext';
import { DialogField, fieldClasses } from '@/components/admin/DialogChrome';
import { useListPagination } from '@/components/admin/useListPagination';
import { compareText, useListSort, type ListSortCompare } from '@/components/admin/useListSort';
import { useTrackedForm } from '@/components/admin/useTrackedForm';
import { listForwardMerchants, type ForwardMerchant } from '@/services/admin.service';
import { getErrorMessage } from '@/services/apiClient';
import {
  POS_TERMINAL_HSN_MAX,
  POS_TERMINAL_TITLE_MAX,
  createPosTerminal,
  deletePosTerminal,
  listPosTerminals,
  restorePosTerminal,
  updatePosTerminal,
  type AdminPosTerminal,
} from '@/services/posTerminalsAdmin.service';
import { toast } from '@/store/toastStore';

const COLUMNS: AdminListColumn[] = [
  { key: 'title', header: 'Title', sortable: true },
  { key: 'hsn', header: 'HSN', sortable: true },
  { key: 'merchant', header: 'Merchant', sortable: true },
  ACTIONS_COLUMN,
];

/** A Locked page has no row actions, so no Actions column. */
const LOCKED_COLUMNS = COLUMNS.filter((c) => c !== ACTIONS_COLUMN);

function terminalTitle(t: AdminPosTerminal): string {
  return t.title.trim() || `Terminal #${t.id}`;
}

const SORT_COMPARE: ListSortCompare<AdminPosTerminal> = {
  title: (a, b) => compareText(terminalTitle(a), terminalTitle(b)),
  hsn: (a, b) => compareText(a.hsn, b.hsn),
  merchant: (a, b) => compareText(a.merchantTitle, b.merchantTitle),
};

export function PosTerminalsAdmin() {
  const locked = useAdminPageLevel() === 'locked';
  const [terminals, setTerminals] = useState<AdminPosTerminal[]>([]);
  const [merchants, setMerchants] = useState<ForwardMerchant[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [showDeleted, setShowDeleted] = useState(false);
  const [editing, setEditing] = useState<AdminPosTerminal | 'new' | null>(null);
  const [deleting, setDeleting] = useState<AdminPosTerminal | null>(null);

  const load = useCallback(async (quiet = false) => {
    if (!quiet) setLoading(true);
    try {
      const [list, forward] = await Promise.all([listPosTerminals(), listForwardMerchants(true)]);
      setTerminals(list);
      setMerchants(forward);
    } catch (err) {
      toast.error(getErrorMessage(err, 'Failed to load terminals'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const query = search.trim().toLowerCase();
  const rows = useMemo(
    () =>
      terminals.filter(
        (t) => (showDeleted || t.active) && (query === '' || t.title.toLowerCase().includes(query) || t.hsn.toLowerCase().includes(query)),
      ),
    [terminals, showDeleted, query],
  );
  const sorting = useListSort(rows, { initialKey: 'title', compare: SORT_COMPARE });
  const pagination = useListPagination(sorting.sorted, { resetOn: [query, showDeleted] });

  async function handleDelete() {
    if (!deleting) return;
    const { id } = deleting;
    try {
      await deletePosTerminal(id);
      setTerminals((prev) => prev.map((t) => (t.id === id ? { ...t, active: false } : t)));
      toast.success('Terminal deleted');
    } catch (err) {
      toast.error(getErrorMessage(err, 'The terminal could not be deleted.'));
    }
  }

  async function handleRestore(t: AdminPosTerminal) {
    try {
      await restorePosTerminal(t.id);
      setTerminals((prev) => prev.map((x) => (x.id === t.id ? { ...x, active: true } : x)));
      toast.success('Terminal restored');
    } catch (err) {
      toast.error(getErrorMessage(err, 'The terminal could not be restored.'));
    }
  }

  return (
    <AdminListPage
      title="POS Terminals"
      subtitle="The card terminals the practice takes payment on, and the merchant each one charges through."
      actions={
        <Button leftIcon={<Plus className="h-4 w-4" />} onClick={() => setEditing('new')}>
          Add terminal
        </Button>
      }
      toolbar={
        <AdminListToolbar
          search={search}
          onSearchChange={setSearch}
          searchPlaceholder="Search terminals..."
          searchLabel="Search terminals"
          showDeleted={showDeleted}
          onShowDeletedChange={setShowDeleted}
          showDeletedId="pos-terminals-show-deleted"
        />
      }
    >
      {loading ? (
        <SpinnerOverlay label="Loading terminals…" />
      ) : rows.length === 0 ? (
        <AdminListEmpty
          title={query ? 'No terminals found' : 'No terminals yet'}
          hint={query ? 'Try adjusting your search.' : locked ? undefined : 'Add a terminal to get started.'}
        />
      ) : (
        <>
          <AdminListTable columns={locked ? LOCKED_COLUMNS : COLUMNS} aria-label="POS terminals" sort={sorting.sort} onSortChange={sorting.onSortChange}>
            {pagination.pageItems.map((t) => {
              const deleted = !t.active;
              const title = terminalTitle(t);
              return (
                <tr
                  key={t.id}
                  data-terminal-id={t.id}
                  className={adminRowClass({ deleted, clickable: !deleted })}
                  onClick={deleted ? undefined : () => setEditing(t)}
                >
                  <td className={ADMIN_TD_CLASS}>
                    <AdminRowName deleted={deleted} nameTestId="pos-terminal-title">
                      {title}
                    </AdminRowName>
                  </td>
                  <td className={`${ADMIN_TD_CLASS} font-mono text-xs`}>{t.hsn}</td>
                  <td className={ADMIN_TD_CLASS}>{t.merchantTitle || <span className="text-amber-700">No merchant</span>}</td>
                  {!locked && (
                    <td className={ADMIN_TD_CLASS}>
                      <AdminRowActions>
                        {deleted ? (
                          <AdminRowAction kind="restore" label={`Restore ${title}`} onClick={() => void handleRestore(t)} />
                        ) : (
                          <>
                            <AdminRowAction kind="edit" label={`Edit ${title}`} onClick={() => setEditing(t)} />
                            <AdminRowAction kind="delete" label={`Delete ${title}`} onClick={() => setDeleting(t)} />
                          </>
                        )}
                      </AdminRowActions>
                    </td>
                  )}
                </tr>
              );
            })}
          </AdminListTable>
          <PaginationFooter pagination={pagination} noun={['terminal', 'terminals']} />
        </>
      )}

      {editing && (
        <PosTerminalFormModal
          key={editing === 'new' ? 'new' : editing.id}
          terminal={editing === 'new' ? null : editing}
          merchants={merchants}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            void load(true);
          }}
        />
      )}

      <ConfirmDialog
        isOpen={deleting !== null}
        onClose={() => setDeleting(null)}
        onConfirm={handleDelete}
        title={deleting ? `Delete "${terminalTitle(deleting)}"?` : ''}
        message="It will no longer be offered when charging a card. You can restore it from Show deleted."
        confirmLabel="Delete terminal"
        variant="danger"
      />
    </AdminListPage>
  );
}

function PosTerminalFormModal({
  terminal,
  merchants,
  onClose,
  onSaved,
}: {
  terminal: AdminPosTerminal | null;
  merchants: ForwardMerchant[];
  onClose: () => void;
  onSaved: () => void;
}) {
  const isNew = terminal === null;
  // Locked: AdminFormModal makes it a viewer; the merchant field invites no pick or add.
  const locked = useAdminPageLevel() === 'locked';
  const live = merchants.filter((m) => m.active);
  const t = useTrackedForm(
    () => ({
      title: terminal?.title ?? '',
      hsn: terminal?.hsn ?? '',
      merchantId: terminal ? terminal.merchantId : (live.find((m) => m.isDefault) ?? live[0])?.accountForwardID ?? 0,
    }),
    { tracking: !isNew },
  );
  const [saving, setSaving] = useState(false);

  // The live merchants, plus the terminal's own when it has been switched off.
  const current = terminal ? merchants.find((m) => m.accountForwardID === terminal.merchantId) : undefined;
  const merchantOptions = [
    ...live.map((m) => ({ label: m.friendlyTitle || `Merchant #${m.accountForwardID}`, value: m.accountForwardID })),
    ...(current && !current.active ? [{ label: `${current.friendlyTitle || `Merchant #${current.accountForwardID}`} (switched off)`, value: current.accountForwardID }] : []),
  ];

  const missingTitle = t.form.title.trim() === '';
  const missingHsn = t.form.hsn.trim() === '';
  const missingMerchant = !merchantOptions.some((o) => o.value === t.form.merchantId);
  const errors = [
    ...(missingTitle ? ['Title is required'] : []),
    ...(missingHsn ? ['HSN is required'] : []),
    ...(missingMerchant ? ['Merchant is required'] : []),
  ];

  async function save() {
    if (errors.length > 0) return;
    setSaving(true);
    try {
      const data = { title: t.form.title, hsn: t.form.hsn, merchantId: t.form.merchantId };
      if (terminal) await updatePosTerminal(terminal.id, data);
      else await createPosTerminal(data);
      toast.success(isNew ? 'Terminal created' : 'Terminal updated');
      onSaved();
    } catch (err) {
      toast.error(getErrorMessage(err, 'The terminal could not be saved.'));
    } finally {
      setSaving(false);
    }
  }

  return (
    <AdminFormModal
      title={isNew ? 'Add Terminal' : 'Edit Terminal'}
      isNew={isNew}
      dirty={isNew ? !missingTitle || !missingHsn : t.dirty}
      saving={saving}
      errors={errors}
      onSave={() => void save()}
      onRevertAll={t.revertAll}
      onClose={onClose}
      size="md"
    >
      <div className="grid gap-4">
        <DialogField
          label="Title"
          htmlFor="pos-terminal-title"
          required
          state={missingTitle ? 'error' : 'none'}
          labelHint={missingTitle ? '— required' : undefined}
          badge={t.badge('title', 'Title')}
        >
          <Input
            id="pos-terminal-title"
            value={t.form.title}
            placeholder="e.g. Front desk"
            maxLength={POS_TERMINAL_TITLE_MAX}
            onChange={(e) => t.patch({ title: e.target.value })}
            className={fieldClasses.input(missingTitle ? 'error' : 'none')}
          />
        </DialogField>
        <DialogField
          label="HSN"
          htmlFor="pos-terminal-hsn"
          required
          state={missingHsn ? 'error' : 'none'}
          labelHint={missingHsn ? '— required' : undefined}
          badge={t.badge('hsn', 'HSN')}
          description="The serial number on the terminal."
        >
          <Input
            id="pos-terminal-hsn"
            value={t.form.hsn}
            placeholder="e.g. 1850000000"
            maxLength={POS_TERMINAL_HSN_MAX}
            onChange={(e) => t.patch({ hsn: e.target.value })}
            className={fieldClasses.input(missingHsn ? 'error' : 'none')}
          />
        </DialogField>
        <DialogField
          label="Merchant"
          htmlFor="pos-terminal-merchant"
          required
          state={missingMerchant ? 'error' : 'none'}
          labelHint={missingMerchant ? '— required' : undefined}
          badge={t.badge('merchantId', 'Merchant')}
          description={
            merchantOptions.length === 0 && !locked
              ? 'The practice has no merchants yet. Add one under Payments > Forward > Merchants first.'
              : 'The merchant account this terminal charges cards through.'
          }
        >
          <Select<number>
            id="pos-terminal-merchant"
            options={merchantOptions}
            value={missingMerchant ? null : t.form.merchantId}
            placeholder={locked ? 'No merchant' : 'Select a merchant'}
            onChange={(v) => t.patch({ merchantId: v ?? 0 })}
          />
        </DialogField>
      </div>
    </AdminFormModal>
  );
}
